import { INCLUDED_ADDON_KEYS } from '@/lib/realEstatePackages';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { adminAPI } from '@/lib/adminApi';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const PACKAGE_ORDER = ['signature', 'first_impressions', 'creator_spotlight'];
const TIME_WINDOWS = [
  { value: 'morning', label: 'Morning (8am–12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm–5pm)' },
  { value: 'flexible', label: 'Flexible' },
];
const OCCUPANCY = [
  { value: 'vacant', label: 'Vacant' },
  { value: 'owner_occupied', label: 'Owner occupied' },
  { value: 'tenant_occupied', label: 'Tenant occupied' },
];
const inputCls = 'bg-[#050816] border-white/10 text-white placeholder:text-white/30 [color-scheme:dark]';

const autoPackage = (sqft) => (!sqft ? '' : sqft < 2000 ? 'signature' : sqft <= 3000 ? 'first_impressions' : 'creator_spotlight');

const Field = ({ label, children, className = '' }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-[#E8E4DE]/80">{label}</Label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <Card className="bg-[#0f1724] border-white/10">
    <CardHeader className="pb-3"><CardTitle className="text-base text-white font-normal">{title}</CardTitle></CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const SimpleSelect = ({ value, onChange, options, testId, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={inputCls} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-[#0f1724] border-white/10 text-white">
      {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function AdminCreateClientOrder() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [client, setClient] = useState(null);
  const [packages, setPackages] = useState({});
  const [addons, setAddons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [packageTouched, setPackageTouched] = useState(false);
  const [f, setF] = useState({
    street: '', unit: '', city: 'Kingman', state: 'AZ', zip_code: '', square_footage: '',
    package: '', add_ons: [], date: '', time_window: 'morning', occupancy: 'vacant',
    lockbox_code: '', access_notes: '', comments: '', skip_email: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!isAdmin) return;
    adminAPI.getUser(userId).then(setClient).catch((e) => toast.error(e.message));
    fetch(`${API_BASE}/api/real-estate/pricing`).then((r) => r.json()).then((d) => setPackages(d.packages || {}));
    authFetch(`${API_BASE}/api/admin/settings/addons/enabled`).then((r) => (r.ok ? r.json() : [])).then(setAddons);
  }, [isAdmin, userId]);

  const sqft = parseInt(f.square_footage, 10) || 0;
  useEffect(() => {
    if (!packageTouched) set('package', autoPackage(sqft));
  }, [sqft, packageTouched]);

  const packageOptions = useMemo(() => PACKAGE_ORDER.filter((k) => packages[k]).map((k) => ({
    value: k, label: `${packages[k].name} — ${packages[k].price_display} (${packages[k].description})`,
  })), [packages]);

  const toggleAddon = (key) => set('add_ons', f.add_ons.includes(key) ? f.add_ons.filter((a) => a !== key) : [...f.add_ons, key]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.package || !f.date) { toast.error('Please choose a package and preferred date'); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/clients/${userId}/orders/real-estate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_address: { street: f.street, unit: f.unit || null, city: f.city, state: f.state, zip_code: f.zip_code },
          square_footage: sqft,
          package: f.package,
          add_ons: f.add_ons,
          preferred_date_1: { date: f.date, time_window: f.time_window },
          occupancy: f.occupancy,
          lockbox_code: f.lockbox_code || null,
          access_notes: f.access_notes || null,
          comments: f.comments || null,
          send_client_email: !f.skip_email,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(Array.isArray(data.detail) ? 'Please check the highlighted fields' : data.detail || 'Failed to create order');
      toast.success(`Order created for ${client?.full_name || client?.email}${f.skip_email ? ' (no client email)' : ''}`);
      navigate(`/admin/real-estate-orders/${data.id}?schedule=1`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return <Navigate to="/orders" replace />;

  return (
    <div className="min-h-screen bg-[#050816] pb-16">
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2">
            <Link to={`/admin/clients/${userId}`} data-testid="back-to-client-link"><ArrowLeft className="mr-2 h-4 w-4" /> Back to client</Link>
          </Button>
          <h1 className="text-2xl font-light text-white tracking-wide flex items-center gap-3">
            <Camera className="h-6 w-6 text-[#C6A14A]" /> New Real Estate Order
          </h1>
          <p className="text-[#E8E4DE]/60 mt-1" data-testid="order-client-name">
            For {client ? `${client.full_name || client.email} · ${client.email}` : '…'}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="admin-create-order-form">
        <Section title="Property">
          <div className="grid sm:grid-cols-4 gap-3">
            <Field label="Street" className="sm:col-span-3">
              <Input required value={f.street} onChange={(e) => set('street', e.target.value)} className={inputCls} placeholder="123 Main St" data-testid="order-street-input" />
            </Field>
            <Field label="Unit">
              <Input value={f.unit} onChange={(e) => set('unit', e.target.value)} className={inputCls} placeholder="Apt 2" data-testid="order-unit-input" />
            </Field>
            <Field label="City" className="sm:col-span-2">
              <Input required value={f.city} onChange={(e) => set('city', e.target.value)} className={inputCls} data-testid="order-city-input" />
            </Field>
            <Field label="State">
              <Input required minLength={2} value={f.state} onChange={(e) => set('state', e.target.value)} className={inputCls} data-testid="order-state-input" />
            </Field>
            <Field label="ZIP">
              <Input required minLength={5} maxLength={10} value={f.zip_code} onChange={(e) => set('zip_code', e.target.value)} className={inputCls} data-testid="order-zip-input" />
            </Field>
          </div>
          <Field label="Square footage">
            <Input required type="number" min={1} value={f.square_footage} onChange={(e) => set('square_footage', e.target.value)} className={inputCls} placeholder="1850" data-testid="order-sqft-input" />
          </Field>
        </Section>

        <Section title="Package & add-ons">
          <Field label="Package">
            <SimpleSelect value={f.package} onChange={(v) => { setPackageTouched(true); set('package', v); }}
              options={packageOptions} testId="order-package-select" placeholder="Enter sq ft or choose a package" />
          </Field>
          {addons.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-2">
              {addons.filter((a) => !INCLUDED_ADDON_KEYS.includes(a.key)).map((a) => (
                <label key={a.key} className="flex items-center gap-2 p-2.5 rounded-lg border border-white/10 bg-[#050816] cursor-pointer text-sm text-[#E8E4DE]">
                  <Checkbox checked={f.add_ons.includes(a.key)} onCheckedChange={() => toggleAddon(a.key)} data-testid={`order-addon-${a.key}`} />
                  <span className="flex-1">{a.name}</span>
                  <span className="text-[#C6A14A]">${(a.price_cents / 100).toFixed(0)}</span>
                </label>
              ))}
            </div>
          )}
        </Section>

        <Section title="Schedule & access">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Preferred date">
              <Input required type="date" value={f.date} onChange={(e) => set('date', e.target.value)} className={inputCls} data-testid="order-date-input" />
            </Field>
            <Field label="Preferred time">
              <SimpleSelect value={f.time_window} onChange={(v) => set('time_window', v)} options={TIME_WINDOWS} testId="order-time-select" />
            </Field>
            <Field label="Occupancy">
              <SimpleSelect value={f.occupancy} onChange={(v) => set('occupancy', v)} options={OCCUPANCY} testId="order-occupancy-select" />
            </Field>
            <Field label="Lockbox code (encrypted)">
              <Input value={f.lockbox_code} maxLength={50} autoComplete="off" onChange={(e) => set('lockbox_code', e.target.value)} className={inputCls} data-testid="order-lockbox-input" />
            </Field>
          </div>
          <Field label="Access notes">
            <Textarea rows={2} maxLength={1000} value={f.access_notes} onChange={(e) => set('access_notes', e.target.value)} className={inputCls} data-testid="order-access-notes-input" />
          </Field>
          <Field label="Notes">
            <Textarea rows={3} maxLength={2000} value={f.comments} onChange={(e) => set('comments', e.target.value)} className={inputCls} data-testid="order-notes-input" />
          </Field>
        </Section>

        <label className="flex items-center gap-2 text-sm text-[#E8E4DE]">
          <Checkbox checked={f.skip_email} onCheckedChange={(v) => set('skip_email', !!v)} data-testid="order-skip-email-checkbox" />
          Skip the client confirmation email
        </label>

        <Button type="submit" disabled={saving} className="w-full sm:w-auto bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="admin-create-order-submit-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create Order & Schedule
        </Button>
      </form>
    </div>
  );
}
